import { useState } from "react";
import { PortalNotification, PriorityType } from "../model/PortalNotification";

import { usePortalNotificationContext } from "../context/PortalNotificationProvider";
import { MarkReadBtn } from "./sections/MarkReadBtn";
import clsx from "clsx";
import PortalNotificationActionArea from "./sections/PortalNotificationActionArea";
import ViewButton from "./sections/ViewBtn";
import { useLocation } from "react-router-dom";
import { PATH_NOTIFICATIONS } from "../../../config/path-config";

// master kill-switch for showing these banners
const pnBannersEnabled = false;

export default function PortalNotificationBanner() {
  const {
    showHighPriorityToolbar,
    showMediumPriorityToolbar,
    highPriorityPortalNotification,
    mediumPriorityPortalNotification,
  } = usePortalNotificationContext();

  const onNotificationsPage =
    useLocation().pathname === PATH_NOTIFICATIONS.ROOT;

  if (onNotificationsPage || pnBannersEnabled) {
    return null;
  }

  return (
    <div className="notification-banner-container">
      {showHighPriorityToolbar &&
        highPriorityPortalNotification.length > 0 &&
        highPriorityPortalNotification.map((notification) => (
          <NotificationBanner
            notification={notification}
            key={notification.id}
            className={clsx("banner-priority-high text-error-darker")}
          />
        ))}
      {showMediumPriorityToolbar &&
        mediumPriorityPortalNotification.length > 0 &&
        mediumPriorityPortalNotification.map((notification) => (
          <NotificationBanner
            notification={notification}
            key={notification.id}
            className={clsx("banner-priority-medium text-warning-darker")}
          />
        ))}
    </div>
  );
}

function NotificationBanner({
  notification,
  className,
}: {
  notification: PortalNotification;
  className?: string;
}) {
  const { markRead } = usePortalNotificationContext();
  const {
    subject,
    message,
    id,
    hasLink,
    actionLinkUrl,
    actionLinkText,
    priority,
  } = notification;
  const [viewNewsletters, setViewNewsletters] = useState(false);
  const handleViewButtonClick = () => setViewNewsletters((prev) => !prev);
  const handleRead = () => markRead([id]);

  return (
    <div
      className={clsx(
        "w-full",
        "relative border-l-8 px-4 py-2.5 flex flex-col transition-all duration-200",
        {
          "bg-error-lighter border-error-main dark:bg-error-dark/50":
            priority === PriorityType.High,
          "bg-warning-lighter border-warning-main dark:bg-warning-dark/40":
            priority === PriorityType.Medium,
        },
        className
      )}
    >
      <div className="flex flex-row items-center justify-between w-full flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          {/* Priority icon */}
          <span
            className={clsx("w-5 h-5 flex-shrink-0", {
              "icon-[mdi--alert-circle]": priority === PriorityType.High,
              "icon-[mdi--alert]": priority === PriorityType.Medium,
            })}
          />
          {/* Title */}
          <span className={clsx("text-base font-semibold")} title={subject}>
            {subject}
          </span>
        </div>
        <div className="self-end">
          {message ? (
            <ViewButton
              onClick={handleViewButtonClick}
              open={viewNewsletters}
            />
          ) : (
            <MarkReadBtn read={handleRead} />
          )}
        </div>
      </div>

      {/* Slide-down details area */}
      {message && (
        <div
          className={clsx(
            "overflow-hidden w-full transition-all duration-300 ease-in-out",
            viewNewsletters
              ? "max-h-[1000px] opacity-100 mt-1.5"
              : "max-h-0 opacity-0 pointer-events-none"
          )}
        >
          {/* Message */}
          <div className="flex flex-row gap-2 items-start pb-1.5">
            <span className="text-sm leading-normal font-normal whitespace-pre-wrap">
              {message}
            </span>
          </div>

          {/* Actions and mark as read */}
          <div className="flex items-center justify-between gap-2 pt-1 w-full mt-1.5">
            <div className="flex items-center gap-2">
              {hasLink && (
                <PortalNotificationActionArea
                  to={actionLinkUrl}
                  text={actionLinkText}
                />
              )}
            </div>
            <div className="flex items-center gap-2">
              <MarkReadBtn read={handleRead} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
